import { makeStyles, Typography, Paper, Box, Link, Container, AppBar, Toolbar } from '@material-ui/core';

// Badal by TELUS brand colors
const badalPrimaryBlue = '#1e87f0';
const badalSecondaryGreen = '#32d296';
const badalTeal = '#5bbad5';

const useStyles = makeStyles(theme => ({
  root: {
    minHeight: '100vh',
    backgroundColor: '#f8f9fa',
  },
  appBar: {
    background: `linear-gradient(135deg, ${badalPrimaryBlue} 0%, ${badalTeal} 100%)`,
    color: '#ffffff',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  logo: {
    height: 36,
    filter: 'brightness(0) invert(1)',
  },
  headerTitle: {
    fontWeight: 600,
    letterSpacing: '-0.01em',
  },
  byTelus: {
    fontSize: '0.75rem',
    opacity: 0.9,
    marginLeft: theme.spacing(1),
    fontWeight: 400,
  },
  content: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  paper: {
    padding: theme.spacing(4),
    maxWidth: 900,
    margin: '0 auto',
  },
  title: {
    marginBottom: theme.spacing(1),
  },
  subtitle: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(3),
  },
  section: {
    marginBottom: theme.spacing(4),
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 600,
  },
  paragraph: {
    marginBottom: theme.spacing(2),
    lineHeight: 1.7,
  },
  list: {
    marginBottom: theme.spacing(2),
    paddingLeft: theme.spacing(3),
    '& li': {
      marginBottom: theme.spacing(1),
      lineHeight: 1.6,
    },
  },
  lastUpdated: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(3),
  },
  footer: {
    marginTop: theme.spacing(4),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  warning: {
    backgroundColor: 'rgba(255, 167, 38, 0.1)',
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
    border: '1px solid #ffa726',
  },
  footerBranding: {
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: '1px solid #e0e0e0',
    textAlign: 'center',
  },
  brandAccent: {
    color: badalPrimaryBlue,
    fontWeight: 600,
  },
  linkAccent: {
    color: badalPrimaryBlue,
    '&:hover': {
      color: badalSecondaryGreen,
    },
  },
}));

export const TermsOfService = () => {
  const classes = useStyles();

  return (
    <Box className={classes.root}>
      <AppBar position="static" className={classes.appBar} elevation={2}>
        <Toolbar className={classes.toolbar}>
          <img src="/badal-logo.png" alt="Badal" className={classes.logo} />
          <Typography variant="h6" className={classes.headerTitle}>
            Internal Developer Portal
            <span className={classes.byTelus}>by TELUS</span>
          </Typography>
        </Toolbar>
      </AppBar>
      <Container className={classes.content}>
        <Paper className={classes.paper}>
          <Typography variant="h4" className={classes.title}>
            Terms of Service
          </Typography>
          <Typography variant="subtitle1" className={classes.subtitle}>
            Badal Internal Developer Portal
          </Typography>
          <Typography variant="body2" className={classes.lastUpdated}>
            Last Updated: January 2025
          </Typography>

          <Box className={classes.section}>
            <Typography variant="h5" className={classes.sectionTitle}>
              1. Acceptance of Terms
            </Typography>
            <Typography className={classes.paragraph}>
              By accessing and using the Badal Internal Developer Portal ("the Portal"), you agree
              to be bound by these Terms of Service and all applicable policies, including the
              TELUS Security Acceptable Use Policy. If you do not agree to these terms, do not
              use the Portal.
            </Typography>
            <Typography className={classes.paragraph}>
              This Portal is provided exclusively for authorized Badal and TELUS employees for
              internal development purposes. Unauthorized access is prohibited.
            </Typography>
          </Box>

          <Box className={classes.section}>
            <Typography variant="h5" className={classes.sectionTitle}>
              2. Authorized Use
            </Typography>
            <Typography className={classes.paragraph}>
              The Portal is provided to support your work responsibilities. Authorized uses include:
            </Typography>
            <ul className={classes.list}>
              <li>Accessing and managing software catalogs and documentation</li>
              <li>Viewing and interacting with GitHub repositories you have access to</li>
              <li>Managing GCP projects and resources within your authorization scope</li>
              <li>Using scaffolding tools to create new projects and components</li>
              <li>Accessing team links, onboarding resources, and development tools</li>
              <li>Customizing your personal Portal preferences and dashboard</li>
            </ul>
          </Box>

          <Box className={classes.section}>
            <Typography variant="h5" className={classes.sectionTitle}>
              3. User Responsibilities
            </Typography>
            <Typography className={classes.paragraph}>
              As a user of the Portal, you are responsible for:
            </Typography>
            <ul className={classes.list}>
              <li><strong>Account Security:</strong> Maintaining the confidentiality of your authentication credentials and not sharing them with others</li>
              <li><strong>Appropriate Use:</strong> Using the Portal only for legitimate business purposes related to your job responsibilities</li>
              <li><strong>Data Protection:</strong> Handling any sensitive or proprietary information accessed through the Portal in accordance with corporate security policies</li>
              <li><strong>Compliance:</strong> Following all applicable corporate policies, including information security and acceptable use policies</li>
              <li><strong>Reporting:</strong> Promptly reporting any security incidents, unauthorized access, or policy violations</li>
            </ul>
          </Box>

          <Box className={classes.section}>
            <Typography variant="h5" className={classes.sectionTitle}>
              4. Prohibited Activities
            </Typography>
            <Box className={classes.warning}>
              <Typography variant="body2">
                <strong>Important:</strong> Violation of these prohibitions may result in disciplinary action,
                including termination of employment and/or legal action.
              </Typography>
            </Box>
            <Typography className={classes.paragraph}>
              The following activities are strictly prohibited:
            </Typography>
            <ul className={classes.list}>
              <li>Attempting to access resources, systems, or data beyond your authorization</li>
              <li>Sharing or exposing authentication tokens, API keys, or credentials</li>
              <li>Circumventing or attempting to circumvent security controls</li>
              <li>Using the Portal to access, store, or transmit malicious software</li>
              <li>Extracting or exfiltrating proprietary code, data, or information for unauthorized purposes</li>
              <li>Using the Portal for personal projects unrelated to Badal or TELUS business</li>
              <li>Attempting to disrupt or degrade Portal services or performance</li>
              <li>Misrepresenting your identity or impersonating other users</li>
              <li>Using automated tools to scrape or harvest data from the Portal without authorization</li>
              <li>Accessing the Portal from unauthorized devices or networks</li>
            </ul>
          </Box>

          <Box className={classes.section}>
            <Typography variant="h5" className={classes.sectionTitle}>
              5. Information Security
            </Typography>
            <Typography className={classes.paragraph}>
              All users must adhere to information security requirements:
            </Typography>
            <ul className={classes.list}>
              <li><strong>Classification:</strong> Treat all information accessed through the Portal according to its classification level</li>
              <li><strong>Need-to-Know:</strong> Only access information necessary for your job responsibilities</li>
              <li><strong>Secure Handling:</strong> Do not store sensitive information on personal devices or unauthorized cloud services</li>
              <li><strong>Clean Desk:</strong> Do not leave sensitive information visible on your screen when unattended</li>
              <li><strong>Incident Reporting:</strong> Report any suspected security incidents immediately</li>
            </ul>
          </Box>

          <Box className={classes.section}>
            <Typography variant="h5" className={classes.sectionTitle}>
              6. Intellectual Property
            </Typography>
            <Typography className={classes.paragraph}>
              All content, code, documentation, and materials accessible through the Portal are the
              proprietary property of Badal, TELUS, or their respective licensors. You may not:
            </Typography>
            <ul className={classes.list}>
              <li>Copy, modify, or distribute Portal content outside of authorized business use</li>
              <li>Use any trademarks, logos, or branding without authorization</li>
              <li>Remove or alter any copyright or proprietary notices</li>
              <li>Reverse engineer or attempt to derive source code from the Portal</li>
            </ul>
            <Typography className={classes.paragraph}>
              Work products created using the Portal during your employment are subject to your
              employment agreement and corporate intellectual property policies.
            </Typography>
          </Box>

          <Box className={classes.section}>
            <Typography variant="h5" className={classes.sectionTitle}>
              7. Third-Party Integrations
            </Typography>
            <Typography className={classes.paragraph}>
              The Portal integrates with third-party services including GitHub and Google Cloud Platform.
              Your use of these integrations is also subject to:
            </Typography>
            <ul className={classes.list}>
              <li>GitHub Terms of Service and Acceptable Use Policy</li>
              <li>Google Cloud Platform Terms of Service</li>
              <li>Corporate policies governing the use of these services</li>
            </ul>
            <Typography className={classes.paragraph}>
              You are responsible for complying with all applicable terms when using these integrated services.
            </Typography>
          </Box>

          <Box className={classes.section}>
            <Typography variant="h5" className={classes.sectionTitle}>
              8. Monitoring and Audit
            </Typography>
            <Typography className={classes.paragraph}>
              By using the Portal, you acknowledge that:
            </Typography>
            <ul className={classes.list}>
              <li>Your usage may be logged and monitored for security and compliance purposes</li>
              <li>Audit trails may be maintained and reviewed</li>
              <li>Suspicious activities may be investigated</li>
              <li>Information may be provided to appropriate parties in connection with investigations</li>
            </ul>
          </Box>

          <Box className={classes.section}>
            <Typography variant="h5" className={classes.sectionTitle}>
              9. Availability and Support
            </Typography>
            <Typography className={classes.paragraph}>
              The Portal is provided on an "as-is" basis. While we strive to maintain high availability:
            </Typography>
            <ul className={classes.list}>
              <li>We do not guarantee uninterrupted access to the Portal</li>
              <li>Scheduled maintenance may temporarily affect availability</li>
              <li>Features and functionality may change without notice</li>
              <li>Support is provided through standard internal channels</li>
            </ul>
          </Box>

          <Box className={classes.section}>
            <Typography variant="h5" className={classes.sectionTitle}>
              10. Enforcement and Consequences
            </Typography>
            <Typography className={classes.paragraph}>
              Violations of these Terms of Service may result in:
            </Typography>
            <ul className={classes.list}>
              <li>Immediate suspension or termination of Portal access</li>
              <li>Disciplinary action up to and including termination of employment</li>
              <li>Civil or criminal legal action where appropriate</li>
              <li>Reporting to relevant authorities if required by law</li>
            </ul>
            <Typography className={classes.paragraph}>
              If you become aware of any violations, you must report them immediately to your
              manager or the security team.
            </Typography>
          </Box>

          <Box className={classes.section}>
            <Typography variant="h5" className={classes.sectionTitle}>
              11. Changes to Terms
            </Typography>
            <Typography className={classes.paragraph}>
              These Terms of Service may be updated from time to time. Continued use of the Portal
              after changes are posted constitutes acceptance of the updated terms. Significant
              changes will be communicated through appropriate channels.
            </Typography>
          </Box>

          <Box className={classes.section}>
            <Typography variant="h5" className={classes.sectionTitle}>
              12. Governing Policy
            </Typography>
            <Typography className={classes.paragraph}>
              These Terms of Service are governed by and should be read in conjunction with:
            </Typography>
            <ul className={classes.list}>
              <li>TELUS Security Acceptable Use Policy (SECPOL001)</li>
              <li>Your employment agreement</li>
              <li>Corporate information security policies</li>
              <li>Applicable privacy legislation</li>
            </ul>
          </Box>

          <Box className={classes.section}>
            <Typography variant="h5" className={classes.sectionTitle}>
              13. Contact
            </Typography>
            <Typography className={classes.paragraph}>
              Questions about these Terms of Service should be directed to the Badal Platform Team
              or your direct manager.
            </Typography>
          </Box>

          <Box className={classes.footer}>
            <Typography variant="body2" color="textSecondary">
              These Terms of Service apply to the Badal Internal Developer Portal. By using
              this Portal, you confirm that you have read, understood, and agree to these terms.
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
              <Link href="/privacy-policy" className={classes.linkAccent}>Privacy Policy</Link>
            </Typography>
          </Box>
          <Box className={classes.footerBranding}>
            <Typography variant="body2" color="textSecondary">
              © {new Date().getFullYear()} <span className={classes.brandAccent}>Badal</span> by TELUS. All rights reserved.
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};
